import moment from "moment";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { dateToSlice, getMomentDate, NTformatDate } from "../../utils";
import { getDayAvailability, rescheduleSingleAppointment } from "../../store/appointment/appointmentActions";
import { Controller, useForm } from "react-hook-form";
import Calendar from "react-calendar";
import ReactSelect from "react-select";

const RescheduleEvent = ({ setIsReschedule, event, refreshParent }) => {
    const [availableOptions, setAvailableOptions] = useState([]);
    const [loadingAvailability, setLoadingAvailability] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const dispatch = useDispatch();

    const getDayAvailabilityAction = (day) => {
        setLoadingAvailability(true);
        setAvailableOptions([]);
        dispatch(getDayAvailability({
            profile: event.tutor,
            day: NTformatDate(day)
        }))
            .unwrap()
            .then(v => {

                let slices = v.map((slice => {
                    return {
                        value: getMomentDate(slice.date),
                        label: dateToSlice(slice.date)
                    };
                }))
                setAvailableOptions(slices)
            })
            .catch(() => {
                setAvailableOptions([]);
            })
            .finally(() => setLoadingAvailability(false));
    }

    const { handleSubmit, control, watch, register, setValue } = useForm()

    const handleReschedule = (data) => {
        setIsSubmitting(true);
        dispatch(rescheduleSingleAppointment(data))
        .unwrap()
        .then(v => refreshParent())
        .finally(() => setIsSubmitting(false))
    }

    const IssueOptions = [
        {value: 0,label: "The student asked to reschedule"},
        {value: 1,label: "Health issues"},
        {value: 2,label: "I am taking leave"},
        {value: 3,label: "I wish to change my rates"},
        {value: 4,label: "I have another commitment"},
        {value: 5,label: "I have a technical problem"},
        {value: 6,label: "Other"},
    ]

    const selectedDate = watch("date");
    const selectedTime = watch("time");
    const selectedReason = watch("reason");
    const canSubmit = Boolean(selectedDate && selectedTime && selectedReason) && !isSubmitting;

    const selectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: "44px",
            borderColor: state.isFocused ? "#191D51" : "#d9dde5",
            boxShadow: state.isFocused ? "0 0 0 1px #191D51" : "none",
            borderRadius: "12px",
            "&:hover": { borderColor: "#191D51" },
        }),
        menu: (base) => ({
            ...base,
            borderRadius: "12px",
            overflow: "hidden",
        }),
    };

    return <div className="mx-5 mb-5 rounded-2xl border border-nt-grey-light bg-white shadow-sm">
        <form className="flex flex-col gap-5 p-5" onSubmit={handleSubmit((data) => handleReschedule(data))}>
            <div className="border-b border-nt-grey-light pb-4">
                <h3 className="text-nt-blue text-lg font-semibold">Reschedule Course</h3>
                <p className="text-sm text-nt-grey mt-1">
                    Choose a new date and time, then add a short reason for the update.
                </p>
            </div>
            <div className="flex flex-col lg:flex-row gap-4">
                <div className="w-full lg:w-1/2 rounded-xl border border-nt-grey-light p-3 bg-slate-50">
                    <p className="text-xs font-semibold uppercase tracking-wide text-nt-grey mb-2">Pick a date</p>
                    <Controller
                        control={control}
                        name="date"
                        render={({ field: { onChange, value } }) => (
                            <Calendar
                                className={"nt-calendar nt-calendar--reschedule w-full mx-auto"}
                                onChange={(e) => { onChange(e); getDayAvailabilityAction(e); setValue("time", null) }}
                                value={value || null}
                                view='month'
                                formatShortWeekday={(locale, date) => moment(date).format("dd")}
                                minDate={moment().add(1, "day").add(1, "hour").toDate()}
                                maxDate={moment().add(1, "year").add(1, "hour").toDate()}
                            />
                        )}
                    />
                </div>
                <div className="w-full lg:w-1/2">
                    <label className="text-xs font-semibold uppercase tracking-wide text-nt-grey mb-2 block">Available time</label>
                    <Controller
                        control={control}
                        name="time"
                        render={({ field: { onChange, value } }) => (
                            <ReactSelect
                                className="w-full"
                                placeholder="Time"
                                isLoading={loadingAvailability}
                                isDisabled={!selectedDate || loadingAvailability}
                                onChange={onChange}
                                value={value || ''}
                                options={availableOptions}
                                noOptionsMessage={() => (
                                    selectedDate ? "No available times for this day" : "Pick a date first"
                                )}
                                styles={selectStyles}
                            />
                        )}
                    />
                    {!loadingAvailability && selectedDate && availableOptions.length === 0 ? (
                        <p className="text-xs text-nt-grey mt-2">No free slots found for this date. Try another day.</p>
                    ) : null}
                    <label className="text-xs font-semibold uppercase tracking-wide text-nt-grey mt-4 mb-2 block">Reason</label>
                    <Controller
                        control={control}
                        name="reason"
                        render={({ field: { onChange, value } }) => (
                            <ReactSelect
                                className="w-full"
                                placeholder="Reason"
                                onChange={onChange}
                                value={value || ''}
                                options={IssueOptions}
                                styles={selectStyles}
                            />
                        )}
                    />
                    <input name="_id" {...register("_id")} type="hidden" value={event._id} />
                    <label className="text-xs font-semibold uppercase tracking-wide text-nt-grey mt-4 mb-2 block">Message (optional)</label>
                    <textarea
                        name="message"
                        {...register("message")}
                        rows={5}
                        className="w-full border border-nt-grey-light focus:border-nt-blue focus:ring-1 focus:ring-nt-blue rounded-xl p-3 text-sm"
                        placeholder="Add details ..."
                    ></textarea>
                </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-nt-grey-light pt-4">
                <button
                    type="button"
                    className="w-32 bg-nt-grey text-white px-2 py-2.5 rounded-xl hover:opacity-90 transition"
                    onClick={() => setIsReschedule(false)}
                    disabled={isSubmitting}
                >
                    Cancel
                </button>
                <button
                    className={`w-40 text-white px-2 py-2.5 rounded-xl transition ${canSubmit ? "bg-nt-blue hover:opacity-90" : "bg-nt-grey opacity-70 cursor-not-allowed"}`}
                    disabled={!canSubmit}
                >
                    {isSubmitting ? "Saving..." : "Reschedule"}
                </button>
            </div>
        </form>

    </div>
}
export { RescheduleEvent }